import { useId, useState, type KeyboardEvent } from 'react';
import { Button } from '../../../../components/Button';
import { SelectPopover } from '../../../../components/SelectPopover';
import { TextInput } from '../../../../components/TextInput';
import {
  ROSTER_PALETTE_KEYS,
  type RosterEvaluationTrait,
  type RosterHandednessColors,
  type RosterIndicator,
  type RosterSettings,
} from '../../../../types/scholarships';
import {
  HANDEDNESS_LABELS,
  paletteColorVar,
  paletteLabel,
  paletteMarkStyle,
  type Handedness,
} from './rosterPalette';
import { mintOptionId, ROSTER_SETTINGS_LIMITS } from './rosterSettingsDraft';
import { SortableSettingsList } from './SortableSettingsList';
import './rosterManagement.css';

/**
 * The roster-settings sections of Configure roster. Each edits one slice of a draft RosterSettings
 * held by the modal; nothing is saved until the modal's Save.
 */

interface EditorProps {
  settings: RosterSettings;
  onChange: (next: RosterSettings) => void;
  disabled?: boolean;
}

const COLOR_OPTIONS = ROSTER_PALETTE_KEYS.map(paletteLabel);
const colorKeyFromLabel = (label: string): string =>
  ROSTER_PALETTE_KEYS.find((k) => paletteLabel(k) === label) ?? 'black';

/**
 * A colour choice: the palette's names in a SelectPopover, with a swatch beside it, since the
 * menu can only show text. The swatch previews what the choice paints: a name's text shade for a
 * name colour, or the dot itself for an indicator.
 */
const ColorSelect = ({
  id,
  value,
  onChange,
  disabled,
  testId,
  paints,
}: {
  id: string;
  value: string;
  onChange: (key: string) => void;
  disabled?: boolean;
  testId?: string;
  paints: 'name' | 'dot';
}) => (
  <span className="settings-color">
    <span
      className="settings-color__swatch"
      style={paints === 'dot' ? paletteMarkStyle(value) : { backgroundColor: paletteColorVar(value) }}
      aria-hidden="true"
    />
    <SelectPopover
      id={id}
      options={COLOR_OPTIONS}
      value={paletteLabel(value)}
      onChange={(label) => onChange(colorKeyFromLabel(label))}
      placeholder="Color"
      clearable={false}
      disabled={disabled}
      testId={testId}
    />
  </span>
);

/** Add-row shared by every list: a text field and a button, Enter adds rather than submitting. */
const AddRow = ({
  label,
  buttonLabel,
  buttonName,
  onAdd,
  disabled,
  atLimit,
}: {
  label: string;
  buttonLabel: string;
  /** Accessible name when the visible label is as short as "Add" and sits beside others. */
  buttonName?: string;
  onAdd: (name: string) => void;
  disabled?: boolean;
  atLimit: boolean;
}) => {
  const [value, setValue] = useState('');
  const add = () => {
    const name = value.trim();
    if (!name) return;
    onAdd(name);
    setValue('');
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // The modal is a form; Enter here means "add this", not "save everything".
    if (e.key === 'Enter') {
      e.preventDefault();
      add();
    }
  };
  return (
    <div className="settings-add-row">
      <TextInput
        aria-label={label}
        placeholder={label}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        maxLength={ROSTER_SETTINGS_LIMITS.nameLength}
        disabled={disabled || atLimit}
      />
      <Button
        variant="secondary"
        type="button"
        onClick={add}
        disabled={disabled || atLimit || value.trim().length === 0}
        aria-label={buttonName}
      >
        {buttonLabel}
      </Button>
    </div>
  );
};

export const EvaluationTraitsEditor = ({ settings, onChange, disabled }: EditorProps) => {
  const traits = settings.evaluationTraits;
  const setTraits = (evaluationTraits: RosterEvaluationTrait[]) =>
    onChange({ ...settings, evaluationTraits });
  const headingId = useId();

  return (
    <section className="settings-section" aria-labelledby={headingId}>
      <h3 id={headingId} className="configure-limits__group-label">
        Evaluation tags (player profile)
      </h3>
      <p className="configure-limits__hint">
        The tags you grade players on. They show as a pill on the depth chart and in lineups.
        Drag to set the order.
      </p>
      {traits.length === 0 ? (
        <p className="configure-limits__hint">No tags yet.</p>
      ) : (
        <SortableSettingsList
          listId="settings-traits"
          items={traits}
          onReorder={setTraits}
          itemName={(t, i) => t.name.trim() || `tag ${i + 1}`}
          renderItem={(trait, index) => (
            <>
              <span className="settings-list__index">{index + 1}</span>
              <TextInput
                className="settings-list__name"
                aria-label={`Tag ${index + 1} name`}
                value={trait.name}
                maxLength={ROSTER_SETTINGS_LIMITS.nameLength}
                disabled={disabled}
                onChange={(e) =>
                  setTraits(
                    traits.map((t) => (t.id === trait.id ? { ...t, name: e.target.value } : t)),
                  )
                }
              />
              <button
                type="button"
                className="roster-link-button roster-link-button--danger"
                disabled={disabled}
                aria-label={`Delete tag ${trait.name.trim() || index + 1}`}
                onClick={() => setTraits(traits.filter((t) => t.id !== trait.id))}
              >
                Delete
              </button>
            </>
          )}
        />
      )}
      <AddRow
        label="New tag"
        buttonLabel="Add tag"
        disabled={disabled}
        atLimit={traits.length >= ROSTER_SETTINGS_LIMITS.traits}
        onAdd={(name) => setTraits([...traits, { id: mintOptionId(), name }])}
      />
    </section>
  );
};

const HANDEDNESS_SIDES: readonly Handedness[] = ['left', 'right', 'switch'];

export const IndicatorsEditor = ({ settings, onChange, disabled }: EditorProps) => {
  const indicators = settings.indicators;
  const setIndicators = (next: RosterIndicator[]) => onChange({ ...settings, indicators: next });
  const setHandedness = (side: keyof RosterHandednessColors, key: string) =>
    onChange({ ...settings, handednessColors: { ...settings.handednessColors, [side]: key } });
  const headingId = useId();
  const namesId = useId();
  const idPrefix = useId();

  return (
    <section className="settings-section" aria-labelledby={headingId}>
      <h3 id={headingId} className="configure-limits__group-label">
        Player indicators &amp; highlights
      </h3>

      <div className="settings-subsection" role="group" aria-labelledby={namesId}>
        <span id={namesId} className="settings-subsection__title">
          See batting side at a glance
        </span>
        <p className="configure-limits__hint">
          Player names are color-coded by <strong>L, R, or S</strong>, so you can quickly see how
          each player hits.
        </p>
        <div className="settings-handedness">
          {HANDEDNESS_SIDES.map((side) => {
            const selectId = `${idPrefix}-hand-${side}`;
            return (
              <div key={side} className="settings-handedness__item">
                <label htmlFor={selectId} className="text-input__label">
                  {HANDEDNESS_LABELS[side]}
                </label>
                <ColorSelect
                  id={selectId}
                  value={settings.handednessColors[side]}
                  onChange={(key) => setHandedness(side, key)}
                  disabled={disabled}
                  paints="name"
                  testId={`handedness-color-${side}`}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="settings-subsection">
        <span className="settings-subsection__title">Custom indicators (dots)</span>
        <p className="configure-limits__hint">
          Flags you want to see at a glance, like draft risk. A player can carry up to three, shown
          as dots next to their name.
        </p>
        {indicators.length === 0 ? (
          <p className="configure-limits__hint">No indicators yet.</p>
        ) : (
          <SortableSettingsList
            listId="settings-indicators"
            items={indicators}
            onReorder={setIndicators}
            itemName={(i, index) => i.name.trim() || `indicator ${index + 1}`}
            renderItem={(indicator, index) => {
              const colorId = `${idPrefix}-indicator-${indicator.id}`;
              return (
                <>
                  <span className="settings-list__index">{index + 1}</span>
                  <TextInput
                    className="settings-list__name"
                    aria-label={`Indicator ${index + 1} name`}
                    value={indicator.name}
                    maxLength={ROSTER_SETTINGS_LIMITS.nameLength}
                    disabled={disabled}
                    onChange={(e) =>
                      setIndicators(
                        indicators.map((i) =>
                          i.id === indicator.id ? { ...i, name: e.target.value } : i,
                        ),
                      )
                    }
                  />
                  <label htmlFor={colorId} className="roster-sr-only">
                    Color for indicator {index + 1}
                  </label>
                  <ColorSelect
                    id={colorId}
                    value={indicator.color}
                    disabled={disabled}
                    paints="dot"
                    onChange={(key) =>
                      setIndicators(
                        indicators.map((i) => (i.id === indicator.id ? { ...i, color: key } : i)),
                      )
                    }
                  />
                  <button
                    type="button"
                    className="roster-link-button roster-link-button--danger"
                    disabled={disabled}
                    aria-label={`Delete indicator ${indicator.name.trim() || index + 1}`}
                    onClick={() => setIndicators(indicators.filter((i) => i.id !== indicator.id))}
                  >
                    Delete
                  </button>
                </>
              );
            }}
          />
        )}
        <AddRow
          label="New indicator"
          buttonLabel="Add indicator"
          disabled={disabled}
          atLimit={indicators.length >= ROSTER_SETTINGS_LIMITS.indicators}
          onAdd={(name) =>
            setIndicators([...indicators, { id: mintOptionId(), name, color: 'blue' }])
          }
        />
      </div>
    </section>
  );
};

interface OptionChipsProps {
  title: string;
  hint?: string;
  values: string[];
  onChange: (next: string[]) => void;
  /** Display text for a stored value, e.g. a pitch shape's long name. */
  labelFor?: (value: string) => string;
  disabled?: boolean;
  /** Singular noun for the add field and the remove buttons: "arm angle". */
  noun: string;
}

/** A plain list of labels as removable chips, with an add field. Order is the order added. */
export const OptionChipsEditor = ({
  title,
  hint,
  values,
  onChange,
  labelFor = (v) => v,
  disabled,
  noun,
}: OptionChipsProps) => {
  const headingId = useId();
  return (
    <div className="settings-chips" role="group" aria-labelledby={headingId}>
      <span id={headingId} className="settings-subsection__title">
        {title}
      </span>
      {hint && <p className="configure-limits__hint">{hint}</p>}
      {values.length === 0 ? (
        <p className="configure-limits__hint">None yet.</p>
      ) : (
        <ul className="settings-chips__list">
          {values.map((value) => (
            <li key={value} className="settings-chip">
              <span>{labelFor(value)}</span>
              <button
                type="button"
                className="settings-chip__remove"
                disabled={disabled}
                aria-label={`Remove ${labelFor(value)}`}
                onClick={() => onChange(values.filter((v) => v !== value))}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
                  <path
                    d="M2 2l6 6M8 2l-6 6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
      <AddRow
        label={`New ${noun}`}
        buttonLabel="Add"
        buttonName={`Add ${noun}`}
        disabled={disabled}
        atLimit={values.length >= ROSTER_SETTINGS_LIMITS.optionList}
        onAdd={(name) =>
          // A repeat (any case) is dropped here rather than refused later at Save.
          values.some((v) => v.toLowerCase() === name.toLowerCase())
            ? undefined
            : onChange([...values, name])
        }
      />
    </div>
  );
};
